import React, { lazy, Suspense } from 'react';
import {
  Route,
  Navigate,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from 'react-router-dom';
import { useSelector } from 'react-redux';

import ErrorBoundary from './ErrorBoundary';
import { PUBLIC_ROUTE, PRIVATE_ROUTE } from './route.constants';
import Loader from './components/utility/loader';
import siteConfig from './config/site.config';
import PrivateMainLayout from './components/PrivateMainLayout/PrivateMainLayout';
import { getSonyToolIsInit } from './redux-new/sony-tool-prop-slice/sony-tool-prop-slice';
import { Spin } from 'antd';

const publicRoutes = [
  {
    path: PUBLIC_ROUTE.FORGET_PASSWORD,
    element: lazy(() => import('./pages/Pages/ForgotPassword/ForgotPassword')),
  },
  {
    path: PUBLIC_ROUTE.PAGE_404,
    element: lazy(() => import('./pages/Pages/404/404')),
  },
  {
    path: PUBLIC_ROUTE.PAGE_500,
    element: lazy(() => import('./pages/Pages/500/500')),
  },
  {
    path: PUBLIC_ROUTE.ERROR_NETWORK,
    element: lazy(() => import('./pages/Pages/ErrorNetwork/ErrorNetwork')),
  },
  {
    path: PUBLIC_ROUTE.RESET_PASSWORD,
    element: lazy(() => import('./pages/Pages/ResetPassword/ResetPassword')),
  },
  {
    path: PUBLIC_ROUTE.SIGN_IN,
    element: lazy(() => import('./pages/Pages/SignIn/SignIn')),
  },
  {
    path: PUBLIC_ROUTE.SIGN_UP,
    element: lazy(() => import('./pages/Pages/SignUp/SignUp')),
  },
  {
    path: PUBLIC_ROUTE.RESULT,
    element: lazy(() => import('./pages/Result/Result')),
  },
];

const privateAdminRoutes = [
  {
    path: PRIVATE_ROUTE.AFFIX,
    element: lazy(() => import('./pages/Navigation/Affix')),
  },
  {
    path: PRIVATE_ROUTE.ALERT,
    element: lazy(() => import('./pages/Feedback/Alert/Alert')),
  },
  {
    path: PRIVATE_ROUTE.AUTOCOMPLETE,
    element: lazy(() => import('./pages/Forms/AutoComplete/AutoComplete')),
  },
  {
    path: PRIVATE_ROUTE.BACK_TO_TOP,
    element: lazy(() => import('./pages/Navigation/BackToTop')),
  },
  {
    path: PRIVATE_ROUTE.BLANK_PAGE,
    element: lazy(() => import('./pages/BlankPage')),
  },
  {
    path: PRIVATE_ROUTE.BREADCRUMB,
    element: lazy(() => import('./pages/UIElements/Breadcrumb/Breadcrumb')),
  },
  {
    path: PRIVATE_ROUTE.BUTTON,
    element: lazy(() => import('./pages/Forms/Button/Button')),
  },
  {
    path: PRIVATE_ROUTE.CHECKBOX,
    element: lazy(() => import('./pages/Forms/Checkbox/Checkbox')),
  },
  {
    path: PRIVATE_ROUTE.CLOSURE,
    element: lazy(() => import('./pages/Closure/Closure')),
  },
  {
    path: PRIVATE_ROUTE.NEW_CLOSURE,
    element: lazy(() => import('./pages/Closure/NewClosure/NewClosure')),
  },
  {
    path: PRIVATE_ROUTE.WORKHOURS,
    element: lazy(() => import('./pages/WorkHours/WorkHours')),
  },
  {
    path: PRIVATE_ROUTE.NEW_WORKHOURS,
    element: lazy(() => import('./pages/WorkHours/NewWorkHours/NewWorkHours')),
  },
  {
    path: PRIVATE_ROUTE.COMMUNICATION,
    element: lazy(() => import('./pages/PushNotification/PushNotification')),
  },
  {
    path: PRIVATE_ROUTE.NEW_COMMUNICATION,
    element: lazy(() =>
      import('./pages/PushNotification/NewPushNotification/NewPushNotification')
    ),
  },
  {
    path: PRIVATE_ROUTE.CODE_MIRROR,
    element: lazy(() => import('./pages/AdvancedUI/CodeMirror/CodeMirror')),
  },
  {
    path: PRIVATE_ROUTE.CUSTOMER,
    element: lazy(() => import('./pages/Customer/Customer')),
  },
  {
    path: PRIVATE_ROUTE.NEW_CUSTOMER,
    element: lazy(() => import('./pages/Customer/NewCustomer/NewCustomer')),
  },
  {
    path: PRIVATE_ROUTE.DASHBOARD,
    element: lazy(() => import('./pages/Widgets/Widgets')),
    exact: true,
  },
  {
    path: PRIVATE_ROUTE.DROPDOWN,
    element: lazy(() => import('./pages/UIElements/Dropdown/Dropdown')),
  },
  {
    path: PRIVATE_ROUTE.DROPZONE,
    element: lazy(() => import('./pages/AdvancedUI/Dropzone/Dropzone')),
  },
  {
    path: PRIVATE_ROUTE.DUTY,
    element: lazy(() => import('./pages/Duty/Duty')),
  },
  {
    path: PRIVATE_ROUTE.NEW_DUTY,
    element: lazy(() => import('./pages/Duty/NewDuty/NewDuty')),
  },
  {
    path: PRIVATE_ROUTE.EDITOR,
    element: lazy(() => import('./pages/Forms/Editor/Editor')),
  },
  {
    path: PRIVATE_ROUTE.FORM,
    element: lazy(() => import('./pages/Forms/Forms')),
  },
  {
    path: PRIVATE_ROUTE.FORM_WITH_VALIDATION,
    element: lazy(() =>
      import('./pages/Forms/FormsWithValidation/FormsWithValidation')
    ),
  },
  {
    path: PRIVATE_ROUTE.FORM_WITH_STEPPERS,
    element: lazy(() => import('./pages/Forms/StepperForms/StepperForms')),
  },
  {
    path: PRIVATE_ROUTE.FRAPPE_CHART,
    element: lazy(() => import('./pages/Charts/FrappeChart/FrappeCharts')),
  },
  {
    path: PRIVATE_ROUTE.GOOGLE_CHART,
    element: lazy(() => import('./pages/Charts/GoogleChart/GoogleChart')),
  },
  {
    path: PRIVATE_ROUTE.INPUT,
    element: lazy(() => import('./pages/Forms/Input/Input')),
  },
  {
    path: PRIVATE_ROUTE.MENU,
    element: lazy(() => import('./pages/Navigation/NavigationMenu')),
  },
  {
    path: PRIVATE_ROUTE.MESSAGE,
    element: lazy(() => import('./pages/Feedback/Message/Message')),
  },
  {
    path: PRIVATE_ROUTE.MODAL,
    element: lazy(() => import('./pages/Feedback/Modal/Modal')),
  },
  {
    path: PRIVATE_ROUTE.MY_PROFILE,
    element: lazy(() => import('./pages/Pages/MyProfile/MyProfile')),
  },
  // {
  //   path: PRIVATE_ROUTE.NOTIFICATION,
  //   element: lazy(() => import('./pages/Feedback/Notification/Notification')),
  // },
  {
    path: PRIVATE_ROUTE.NOTIFICATION,
    element: lazy(() => import('./pages/Notification/Notification')),
  },
  {
    path: PRIVATE_ROUTE.OP_BADGE,
    element: lazy(() => import('./pages/UIElements/Badge/Badge')),
  },
  {
    path: PRIVATE_ROUTE.OP_CARD,
    element: lazy(() => import('./pages/UIElements/Card/Card')),
  },
  {
    path: PRIVATE_ROUTE.OP_CAROUSEL,
    element: lazy(() => import('./pages/UIElements/Carousel/Carousel')),
  },
  {
    path: PRIVATE_ROUTE.OP_COLLAPSE,
    element: lazy(() => import('./pages/UIElements/Collapse/Collapse')),
  },
  {
    path: PRIVATE_ROUTE.OP_POPOVER,
    element: lazy(() => import('./pages/UIElements/Popover/Popover')),
  },
  {
    path: PRIVATE_ROUTE.OP_TAG,
    element: lazy(() => import('./pages/UIElements/Tag/Tag')),
  },
  {
    path: PRIVATE_ROUTE.OP_TIMELINE,
    element: lazy(() => import('./pages/UIElements/Timeline/Timeline')),
  },
  {
    path: PRIVATE_ROUTE.OP_TOOLTIP,
    element: lazy(() => import('./pages/UIElements/Tooltip/Tooltip')),
  },
  {
    path: PRIVATE_ROUTE.OPERATOR,
    element: lazy(() => import('./pages/Operator/Operator')),
  },
  {
    path: PRIVATE_ROUTE.NEW_OPERATOR,
    element: lazy(() => import('./pages/Operator/NewOperator/NewOperator')),
  },
  {
    path: PRIVATE_ROUTE.PAGINATION,
    element: lazy(() => import('./pages/UIElements/Pagination/Pagination')),
  },
  {
    path: PRIVATE_ROUTE.POPCONFIRM,
    element: lazy(() => import('./pages/Feedback/Popconfirm/Popconfirm')),
  },
  {
    path: PRIVATE_ROUTE.PROGRESS,
    element: lazy(() => import('./pages/Forms/Progress/Progress')),
  },
  {
    path: PRIVATE_ROUTE.RADIOBOX,
    element: lazy(() => import('./pages/Forms/Radiobox/Radiobox')),
  },
  {
    path: PRIVATE_ROUTE.RATING,
    element: lazy(() => import('./pages/UIElements/Rating/Rating')),
  },
  {
    path: PRIVATE_ROUTE.REACT_CHART_2,
    element: lazy(() => import('./pages/Charts/ReactChart2/ReactChart2')),
  },
  {
    path: PRIVATE_ROUTE.REACT_DATES,
    element: lazy(() => import('./pages/AdvancedUI/ReactDates/ReactDates')),
  },
  {
    path: PRIVATE_ROUTE.RECHARTS,
    element: lazy(() => import('./pages/Charts/Recharts/Recharts')),
  },
  {
    path: PRIVATE_ROUTE.SELECT,
    element: lazy(() => import('./pages/Forms/Select/Select')),
  },
  {
    path: PRIVATE_ROUTE.SPIN,
    element: lazy(() => import('./pages/Feedback/Spin/Spin')),
  },
  {
    path: PRIVATE_ROUTE.SWIPER_SLIDER,
    element: lazy(() => import('./pages/UIElements/SwiperSlider/SwiperSlider')),
  },
  {
    path: PRIVATE_ROUTE.TAB,
    element: lazy(() => import('./pages/Forms/Tab/Tab')),
  },
  {
    path: PRIVATE_ROUTE.TABLE,
    element: lazy(() => import('./pages/Tables/AntTables/AntTables')),
  },
  {
    path: PRIVATE_ROUTE.TRANSFER,
    element: lazy(() => import('./pages/Forms/Transfer/Transfer')),
  },
  {
    path: PRIVATE_ROUTE.TREE,
    element: lazy(() => import('./pages/UIElements/Tree/Tree')),
  },
  {
    path: PRIVATE_ROUTE.UPPY,
    element: lazy(() => import('./pages/AdvancedUI/Uppy/Uppy')),
  },
  {
    path: PRIVATE_ROUTE.CALENDAR,
    element: lazy(() => import('./pages/Calendar/Calendar')),
  },
  {
    path: PRIVATE_ROUTE.OPERATOR_UNAVAILABILITY,
    element: lazy(() =>
      import('./pages/OperatorUnavailability/OperatorUnavailability')
    ),
  },
  {
    path: PRIVATE_ROUTE.NEW_OPERATOR_UNAVAILABILITY,
    element: lazy(() =>
      import(
        './pages/OperatorUnavailability/NewOperatorUnavailability/NewOperatorUnavailability'
      )
    ),
  },
];

const privateCustomerRoutes = [
  {
    path: PRIVATE_ROUTE.MY_PROFILE,
    element: lazy(() => import('./pages/Pages/MyProfile/MyProfile')),
  },
];

function PrivateRoute({ children, ...rest }) {
  const auth = useSelector((state) => state.auth);
  const location = useLocation();

  if (auth.isLoggedIn) {
    return children;
  } else {
    return (
      <Navigate
        to={PUBLIC_ROUTE.SIGN_IN}
        replace
        state={{ path: location.pathname }}
      />
    );
  }
}

export default function AppRouter() {
  const auth = useSelector((state) => state.auth);
  const sonyToolIsInit = useSelector(getSonyToolIsInit);

  return !sonyToolIsInit ? (
    <ErrorBoundary>
      <Suspense fallback={<Loader />}>
        <Router basename={siteConfig.basename}>
          <Routes>
            {!auth.isLoggedIn &&
              publicRoutes.map((route, index) => (
                <Route
                  key={'public-' + index}
                  path={route.path}
                  element={<route.element />}
                />
              ))}
            {auth.isLoggedIn &&
            auth.authenticate.scaAccount.scaAccountRole.id !== 3 ? (
              <Route path="/" element={<PrivateMainLayout />}>
                {privateAdminRoutes.map((route, index) => (
                  <Route
                    key={'private-' + index}
                    path={route.path}
                    element={
                      <PrivateRoute>
                        <route.element />
                      </PrivateRoute>
                    }
                  />
                ))}
              </Route>
            ) : (
              <Route path={PRIVATE_ROUTE.MY_PROFILE}>
                {privateCustomerRoutes.map((route, index) => (
                  <Route
                    key={'private-' + index}
                    path={route.path}
                    element={
                      <PrivateRoute>
                        <route.element />
                      </PrivateRoute>
                    }
                  />
                ))}
              </Route>
            )}
            {!auth.isLoggedIn ? (
              <Route
                path="*"
                element={
                  <Navigate
                    to={PUBLIC_ROUTE.SIGN_IN}
                    state={{ from: window.location.pathname }}
                  />
                }
              />
            ) : auth.authenticate.scaAccount.scaAccountRole.id !== 3 ? (
              <Route
                path="*"
                element={
                  <Navigate
                    to={PRIVATE_ROUTE.DASHBOARD}
                    state={{ from: window.location.pathname }}
                  />
                }
              />
            ) : (
              <Route
                path="*"
                element={
                  <Navigate
                    to={PRIVATE_ROUTE.MY_PROFILE}
                    state={{ from: window.location.pathname }}
                  />
                }
              />
            )}
          </Routes>
        </Router>
      </Suspense>
    </ErrorBoundary>
  ) : (
    <div
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
      }}
    >
      <Spin />
    </div>
  );
}
